import {
	Box,
	Divider,
	Drawer,
	Group,
	Image,
	Input,
	Stack,
	Textarea,
	Tooltip,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import type { AgentToolsOut } from '@repo/api-codegen';
import {
	getPersonasQueryKey,
	useDisablePersonaAgentTool,
	useEnablePersonaAgentTool,
	useGetPersonaAgentTools,
	useUpdatePersona,
} from '@repo/api-codegen';
import { AIAvatar } from '@repo/common/components/AI/Avatar';
import {
	SettingsBlock,
	SettingsSwitch,
} from '@repo/common/components/index.ts';
import {
	getToolDescription,
	getToolName,
} from '@repo/common/constants/aiTools';
import {
	Button,
	Icon,
	IconButton,
	Switch,
	Text,
	Title,
} from '@repo/foundations';
import { useDebounceFn } from 'ahooks';
import { capitalize, toLower } from 'lodash-es';
import React, { useCallback, useState } from 'react';
import { queryClient } from '../../../api';
import { useFeatureFlags } from '../../../utils/featureFlags';
import { sentenceCase } from '../../../utils/stringUtils';
import { ImageUpload } from '../../ImageUpload/ImageUpload.tsx';
import { MultiTeamsSelector } from '../../MultiTeamsSelector/MultiTeamsSelector.tsx';
import { MultiGroupSelector } from '../Selectors/MultiGroupSelector';
import { AIPersonasFilters } from './AIPersonasFilters';
import { AIPersonasRunSqlFiltersInline } from './AIPersonasRunSqlFiltersInline';
import { useAIPersonaImage } from './hooks';

const CUSTOM_INSTRUCTIONS_PLACEHOLDER = `Data Sources, Search Parameters, Constraints, Tone and Style`;

interface AIPersonaCardProps {
	id?: string | undefined | null;
	isDefaultPersona?: boolean | undefined | null;
	name?: string | undefined | null;
	description?: string | undefined | null;
	avatarURL?: string | undefined | null;
	teams?: string[] | undefined | null;
	groups?: string[] | undefined | null;
	customInstructions?: string | undefined | null;
	maskPiiOnResponseGeneration?: boolean | undefined | null;
	onDelete: (id: string) => void;
}

export function AIPersonaCard({
	id,
	name,
	description,
	avatarURL,
	teams,
	groups,
	customInstructions,
	maskPiiOnResponseGeneration,
	onDelete,
	isDefaultPersona,
}: AIPersonaCardProps) {
	const [isOpen, { open, close }] = useDisclosure();
	const [personaName, setPersonaName] = useState(name || '');
	const [personaDescription, setPersonaDescription] = useState(
		description || ''
	);
	const [personaCustomInstructions, setPersonaCustomInstructions] = useState(
		customInstructions || ''
	);
	const [personaAvatarUrl, setPersonaAvatarUrl] = useState(avatarURL || '');
	const [personaTeams, setPersonaTeams] = useState<string[]>(teams || []);
	const [personaGroups, setPersonaGroups] = useState<string[]>(groups || []);
	const [personaMaskPii, setPersonaMaskPii] = useState<boolean>(
		maskPiiOnResponseGeneration || false
	);

	const { strictRunSqlPermissions } = useFeatureFlags();

	const { data: personaTools, refetch: refetchPersonaTools } =
		useGetPersonaAgentTools(
			{ pathParams: { personaId: id || '' } },
			{ enabled: !!id && isOpen }
		);
	const { mutateAsync: enablePersonaAgentTool } = useEnablePersonaAgentTool();
	const { mutateAsync: disablePersonaAgentTool } = useDisablePersonaAgentTool();

	const { mutateAsync: updatePersona } = useUpdatePersona({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: getPersonasQueryKey({}),
			});
		},
	});
	const { mutateAsync: uploadImage } = useAIPersonaImage();

	const handleUpdatePersonaDebounced = useDebounceFn(
		() => {
			updatePersona({
				body: {
					name: personaName,
					description: personaDescription,
					custom_instructions: personaCustomInstructions,
					teams: personaTeams,
					groups: personaGroups,
					mask_pii_on_response_generation: personaMaskPii,
				},
				pathParams: {
					personaId: id || '',
				},
			});
		},
		{ wait: 500 }
	);

	const onMakeDefault = useCallback(async () => {
		if (!id) return;
		await updatePersona({
			body: {
				make_default: true,
			},
			pathParams: {
				personaId: id,
			},
		});
	}, [id, updatePersona]);

	const onNameChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			setPersonaName(event.target.value);
			handleUpdatePersonaDebounced.run();
		},
		[handleUpdatePersonaDebounced]
	);

	const onDescriptionChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>) => {
			setPersonaDescription(event.target.value);
			handleUpdatePersonaDebounced.run();
		},
		[handleUpdatePersonaDebounced]
	);

	const onCustomInstructionsChange = useCallback(
		(event: React.ChangeEvent<HTMLTextAreaElement>) => {
			setPersonaCustomInstructions(event.target.value);
			handleUpdatePersonaDebounced.run();
		},
		[handleUpdatePersonaDebounced]
	);

	const onUpload = useCallback(
		async (file: File) => {
			const resp = await uploadImage({
				file: file,
				aiPersonaId: id || '',
			});
			setPersonaAvatarUrl((resp.avatar_url as unknown as string) || '');
		},
		[id, uploadImage]
	);

	const onTeamsChange = useCallback(
		(teamIds: string[]) => {
			setPersonaTeams(teamIds);
			handleUpdatePersonaDebounced.run();
		},
		[handleUpdatePersonaDebounced]
	);

	const onGroupsChange = useCallback(
		(groupIds: string[]) => {
			setPersonaGroups(groupIds);
			handleUpdatePersonaDebounced.run();
		},
		[handleUpdatePersonaDebounced]
	);

	const onMaskPiiChange = useCallback(() => {
		setPersonaMaskPii(!personaMaskPii);
		handleUpdatePersonaDebounced.run();
	}, [personaMaskPii, handleUpdatePersonaDebounced]);

	// SQL tools configuration
	const sqlTools = {
		toolNames: ['run_sql'],
		tools: personaTools?.tools || [],
		uiName: 'run_sql',
	};

	const filteredSqlTools = sqlTools.tools.filter((tool) =>
		sqlTools.toolNames.includes(tool.name)
	);

	const renderPersonaTool = (config: {
		tools: AgentToolsOut['tools'];
		toolNames: string[];
		uiName: string;
	}) => {
		if (config.tools.length === 0) return null;

		const tool = config.tools.find((tool) =>
			config.toolNames.includes(tool.name)
		);

		if (!tool) return null;

		// Create a wrapper click handler to show a notification when clicking on disabled toggle
		const handleToggleClick = () => {
			if (!tool.can_be_enabled) {
				showNotification({
					title: 'Tool disabled globally',
					message:
						'This tool has been disabled globally and cannot be enabled at the persona level',
					color: 'yellow',
				});
				return;
			}

			if (!id) return;

			if (tool.enabled) {
				(disablePersonaAgentTool as any)({
					pathParams: {
						personaId: id,
						toolName: tool.name,
					},
				})
					.then(() => {
						refetchPersonaTools();
					})
					.catch((error: unknown) => {
						showNotification({
							title: 'Error',
							message: 'Failed to disable tool. Please try again.',
							color: 'red',
						});
					});
			} else {
				(enablePersonaAgentTool as any)({
					pathParams: {
						personaId: id,
						toolName: tool.name,
					},
				})
					.then(() => {
						refetchPersonaTools();
					})
					.catch((error: unknown) => {
						showNotification({
							title: 'Error',
							message: 'Failed to enable tool. Please try again.',
							color: 'red',
						});
					});
			}
		};

		const isRunSqlTool = tool.name === 'run_sql';

		return (
			<Tooltip
				disabled={!!tool.can_be_enabled}
				label="This tool has been disabled globally and cannot be enabled at the persona level"
				position="top"
				withArrow
			>
				<div>
					<SettingsBlock
						key={`persona-${tool.name}`}
						title={getToolName(
							tool.name,
							capitalize(toLower(sentenceCase(tool.name)))
						)}
						description={getToolDescription(
							tool.name,
							tool.frontend_description || ''
						)}
						rightComponent={
							<Box onClick={(e) => e.stopPropagation()}>
								<Switch
									checked={tool.enabled}
									onChange={() => handleToggleClick()}
									disabled={!tool.can_be_enabled}
								/>
							</Box>
						}
					>
						{isRunSqlTool && tool.enabled && strictRunSqlPermissions && (
							<Box mt="sm">
								<AIPersonasRunSqlFiltersInline personaId={id || ''} />
							</Box>
						)}
					</SettingsBlock>
				</div>
			</Tooltip>
		);
	};

	return (
		<>
			<Drawer
				position="right"
				opened={isOpen}
				onClose={close}
				title={
					<Group>
						Edit {personaName || 'Untitled Persona'}{' '}
						{isDefaultPersona && (
							<Icon name="starFilled" color="icon/primary/default" />
						)}
					</Group>
				}
				size="xl"
			>
				<Stack spacing="sm">
					<ImageUpload
						callback={onUpload}
						label="Icon"
						placeholder={
							personaAvatarUrl || (
								<AIAvatar size="sm" speed="slow" iconSpeed="slow" />
							)
						}
					/>
					<Input
						name="Name"
						placeholder="Name your Persona"
						value={personaName}
						onChange={onNameChange}
					/>
					<Textarea
						size="xs"
						name="Description"
						onChange={onDescriptionChange}
						placeholder="Optional description of what your Persona does"
						value={personaDescription}
						minRows={3}
						autosize
					/>

					<Divider />
					<Stack spacing={0}>
						<Title weight="semibold" size="sm">
							Custom instructions
						</Title>
						<Text size="xs" color="text/secondary/default">
							Provide custom instructions for the AI to follow when using this
							persona. These will be appended to the global instructions
						</Text>
					</Stack>
					<Textarea
						size="xs"
						name="Custom instructions"
						onChange={onCustomInstructionsChange}
						placeholder={CUSTOM_INSTRUCTIONS_PLACEHOLDER}
						value={personaCustomInstructions}
						minRows={3}
						autosize
					/>
					<Box>
						<AIPersonasFilters personaId={id || ''} />
					</Box>

					<Divider />
					<Stack spacing={0}>
						<Title weight="semibold" size="sm">
							Tools
						</Title>
						<Text size="xs" color="text/secondary/default">
							Enable or disable specific tools for this persona
						</Text>
					</Stack>
					<Box>
						{renderPersonaTool({
							tools: filteredSqlTools,
							toolNames: sqlTools.toolNames,
							uiName: sqlTools.uiName,
						})}
					</Box>

					<Divider />
					<Stack spacing={0}>
						<Title weight="semibold" size="sm">
							Privacy
						</Title>
						<Text size="xs" color="text/secondary/default">
							Control how this persona handles PII data
						</Text>
					</Stack>
					<Box>
						<SettingsSwitch
							disabled={false}
							key={'persona_mask_pii'}
							title={'Mask PII content'}
							checked={personaMaskPii}
							description={
								'When querying resources, try to mask data marked as PII before being processed by Secoda AI'
							}
							onChange={onMaskPiiChange}
						/>
					</Box>

					<Divider />
					<Stack spacing={0}>
						<Title weight="semibold" size="sm">
							Teams and groups
						</Title>
						<Text size="xs" color="text/secondary/default">
							Only members of in any of these teams and groups will be able to
							use this persona (except for Admins who can use all personas)
						</Text>
					</Stack>
					<Stack spacing="xs">
						<MultiTeamsSelector
							label="Teams"
							value={personaTeams}
							setValue={onTeamsChange}
						/>
						<MultiGroupSelector
							label="Groups"
							value={personaGroups}
							setValue={onGroupsChange}
						/>
					</Stack>
					<Divider />
					{!isDefaultPersona && (
						<>
							<Stack spacing={0}>
								<Title weight="semibold" size="sm">
									Make this the default persona
								</Title>
								<Text size="xs" color="text/secondary/default">
									When no persona is selected, this persona will be used.
								</Text>
							</Stack>
							<Group>
								<Button
									onClick={onMakeDefault}
									variant="primary"
									tone="default"
								>
									Make default
								</Button>
							</Group>
							<Divider />
						</>
					)}
					<Stack spacing={0}>
						<Title weight="semibold" size="sm">
							Delete
						</Title>
						<Text size="xs" color="text/secondary/default">
							This action will remove the AI persona and associated data
						</Text>
					</Stack>
					<Group>
						<Button
							onClick={() => onDelete(id || '')}
							variant="primary"
							tone="critical"
						>
							Delete Persona
						</Button>
					</Group>
				</Stack>
			</Drawer>
			<SettingsBlock
				onClickRow={open}
				title={
					<Group noWrap align="flex-start" spacing="xs">
						{personaAvatarUrl ? (
							<Image
								src={personaAvatarUrl}
								width={24}
								height={24}
								radius="xs"
							/>
						) : (
							<AIAvatar size="sm" speed="none" iconSpeed="none" />
						)}
						<Stack justify="center" align="flex-start" spacing={0} w="100%">
							<Text size="xs" weight="semibold">
								<Group>
									{personaName || 'Untitled Persona'}{' '}
									{isDefaultPersona && (
										<Tooltip label="Default persona">
											<Icon name="starFilled" color="icon/primary/default" />
										</Tooltip>
									)}
								</Group>
							</Text>
							{personaDescription && (
								<Text color="text/secondary/default" size="xs">
									{personaDescription}
								</Text>
							)}
						</Stack>
					</Group>
				}
				rightComponentHover={
					<IconButton
						iconName="pencil"
						onClick={open}
						variant="tertiary"
						tooltip="Edit persona"
					/>
				}
			/>
		</>
	);
}
